import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';
import { flex } from '@resources/mixins';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    expanded: {},
    notActive: {},
    mainWrapper:{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
    },
    graphWrapper:{
        flex: '0 0 380px',
        minHeight: 380,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
        // Tablet & phone: the graph is collapsed behind a toggle, so the wrapper
        // hugs its content (toggle button, plus the graph body only when open).
        [theme.down('tablet')]: {
            flex: '0 0 auto',
            minHeight: 0,
        },
    },

    graphBody: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // When revealed on tablet/phone the graph needs an explicit height, since
        // its wrapper no longer reserves vertical space.
        [theme.down('tablet')]: {
            flex: '0 0 auto',
            height: 340,
        },
    },

    graphToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 44,
        margin: `0 0 ${theme.spacing(2.5)}px`,
        padding: `0 ${theme.spacing(4)}px`,
        borderRadius: 22,
        border: `1px solid ${theme.colors.grey_light}`,
        background: theme.colors.surface_elevated,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.primary,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    },

    graphToggleIcon: {
        transition: 'transform .2s ease',
    },

    graphToggleIconOpen: {
        transform: 'scale(1, -1)',
    },

    listWrapper: {
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },

    header: {
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: 64,
        flex: '0 0 64px',
        alignContent: 'center',
        alignItems: 'center',
        padding: `${theme.spacing(3)} 0`,
        // Tablet & phone: stack the title above the filters so the search box and
        // time-frame pills get the full width instead of being cropped in a row.
        [theme.down('tablet')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            minHeight: 0,
            flex: '0 0 auto',
            gap: theme.spacing(2),
            padding: `${theme.spacing(2)}px 0`,
        },
    },
    // JS-applied when the layout is narrow (incl. coarse tablets in landscape
    // that fall just outside the CSS width breakpoint). Stacks the title above
    // full-width filters so the search + time-frame never crop.
    headerNarrow: {
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: 0,
        flex: '0 0 auto',
        gap: theme.spacing(2),
        padding: `${theme.spacing(2)}px 0`,

        '& $filters': {
            width: '100%',
        },
        '& $searchWrapper': {
            maxWidth: 'none',
            flex: '1 1 auto',
        },
    },

    headerTitleGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: 0,
    },

    contentTitle:{
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        fontSize: theme.spacing(5),
        marginLeft: '10px',
        [theme.down('mobile')]: {
            marginLeft: 0,
            fontSize: 18,
        },
    },

    filters: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },

    filterIcon: {
        color: theme.colors.violet_primary,
        cursor: 'pointer',
    },

    searchWrapper: {
        width: '100%',
        maxWidth: 312,
        [theme.down('mobile')]: {
            maxWidth: 'none',
        },
    },

    timeWindowControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },

    timeWindowLabel: {
        color: theme.colors.grey_text,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.35,
        textTransform: 'uppercase',
    },

    timeWindowButtons: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: 4,
        borderRadius: 999,
        background: color(theme.colors.violet_primary).alpha(0.08).toString(),
    },

    timeWindowButton: {
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: theme.colors.violet_primary,
        cursor: 'pointer',
        fontFamily: theme.font.secondary,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        minWidth: 46,
        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
        whiteSpace: 'nowrap',

        '&:disabled': {
            cursor: 'default',
            opacity: 0.6,
        },
    },

    timeWindowButtonActive: {
        extend: 'timeWindowButton',
        background: theme.colors.violet_primary,
        color: theme.colors.white,
    },

    button: {
        border: 'none',
        background: 'none',
        outline: 'none',
        padding: 0,
        height: 24,
        width: 24,
        ...flex('row', 'center', 'center'),

        '&:disabled': {
            opacity: 0.3,
        },

        '&:hover': {
            background: theme.colors.icon_background,
            borderRadius: theme.radius / 2,
            cursor: 'pointer',
        },
    },

    tableWrapper: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
}));
